/* ==========================================================================
   MICHELANGELO MUSIC — playable pads
   Rebuilt 2026-07-25. Upgrades any [data-mm-pads] block.

   THE IDEA
   Every pad draws its own sound. The bars inside a pad are the real decoded
   waveform of the sample it fires, so the kick reads as a fat decaying blob
   and the closed hat reads as a needle — you can see what a pad will do
   before you touch it. Nothing here is decorative: the shape is data.

   On a hit, the bars fill left to right in real time over the sample's true
   duration, driven off AudioContext.currentTime rather than a CSS timer. A
   51ms hat flicks; a 692ms open hat sweeps. The animation cannot drift out
   of sync with the audio because it is reading the same clock.

   The transport plays Mikey's own FL Studio pattern. Isolated one-shots do
   not sell a drum kit — a groove does — so the loop is the argument, and
   the pads light in time with it.

   AUDIO
   Web Audio, not <audio> elements. An <audio> tag carries enough latency to
   feel late on a drum hit and cannot overlap cleanly with itself, so a fast
   roll cuts off its own tail. Decoded buffers fired through BufferSource
   nodes are sample-accurate and naturally polyphonic.

   Loading is staged: nothing downloads on page load; samples fetch and
   decode into a SUSPENDED context when the section nears the viewport
   (decoding needs no user gesture, only playback does); the first tap
   resumes the context, which is the iOS unlock. That ordering is why the
   first hit is instant instead of paying for a round trip.

   The sequencer uses lookahead scheduling — a 25ms timer queues note starts
   up to 120ms into the future against the audio clock. setTimeout alone
   drifts audibly within a couple of bars; this does not.
   ========================================================================== */

(function () {
  'use strict';

  var AudioCtx = window.AudioContext || window.webkitAudioContext;
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var LOOKAHEAD = 0.12;
  var TICK_MS = 25;

  /* ==========================================================================
     THE iOS RINGER SWITCH  (fixed 2026-07-27)

     Web Audio on iOS runs in the "ambient" audio session, and the physical
     mute switch silences that session. So the pads were dead for anyone
     browsing with their ringer off — which on an iPhone is most people, most
     of the time — with no sign anything was wrong: the pad lit up, the
     waveform swept, and no sound came out.

     This is specific to Web Audio. HTMLMediaElement uses the "playback"
     session, which ignores the switch, which is why the Chroma preview
     player (a real <audio> element — see mm-audio.js) was never affected.
     Do not "fix" that file to match this one.

     Two layers, because the clean fix is only about three years old:

       1. navigator.audioSession.type = 'playback' — the W3C Audio Session
          API, shipped in Safari 16.4 (March 2023). Declares that this page
          plays media rather than incidental sound, so the mute switch no
          longer applies. This is the real fix and covers effectively every
          iPhone still in service.

       2. Older iOS: start a silent, looping <audio>. While any media element
          is playing, WebKit promotes the whole page's audio session to
          playback and the Web Audio graph is promoted with it. This is the
          long-standing workaround the audio libraries all carry.

     Both run on the FIRST USER GESTURE, never at load. Claiming a playback
     session on page load would duck whatever the visitor is already
     listening to before they have asked us for a single sound.
     ========================================================================== */
  var IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  /* 44-byte RIFF header plus one near-zero sample, inline as a data URI so
     the unlock cannot be waiting on a network round trip — it has to take
     effect inside the gesture that triggered it. */
  var SILENT_WAV =
    'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';

  var outputUnlocked = false;
  var silentEl = null;

  function unlockOutput() {
    if (outputUnlocked) return;
    outputUnlocked = true;

    if (navigator.audioSession) {
      try { navigator.audioSession.type = 'playback'; } catch (err) {}
      return;
    }

    /* Only iOS needs the fallback. Anywhere else this would be a permanently
       playing media element for no reason — and on Android it can leave a
       phantom entry in the notification shade. */
    if (!IS_IOS) return;

    try {
      silentEl = document.createElement('audio');
      silentEl.setAttribute('playsinline', '');
      silentEl.setAttribute('aria-hidden', 'true');
      silentEl.loop = true;
      silentEl.preload = 'auto';
      silentEl.src = SILENT_WAV;
      var played = silentEl.play();
      if (played && played.catch) played.catch(function () {});
    } catch (err) {}
  }

  /* Transcribed from Mikey's FL Studio export (96 PPQ, 140 BPM, 4/4) — one
     .mid per instrument, track names matching the sample names.

     Stored as an event list rather than an on/off step grid. A grid can only
     say "sixteenth or nothing" at one velocity; offsets in beats survive
     swing, triplets, ghost notes and whatever bar length the next export
     happens to use.

     b = offset in beats from the top of the loop, r = pad role, v = MIDI
     velocity. */
  var LOOP_BEATS = 8;
  var EVENTS = [
    { b: 0,    r: 'hat',     v: 100 },
    { b: 0,    r: 'kick',    v: 100 },
    { b: 0.75, r: 'hat',     v: 100 },
    { b: 1,    r: 'openhat', v: 100 },
    { b: 1.5,  r: 'hat',     v: 100 },
    { b: 2,    r: 'snare',   v: 100 },
    { b: 2.75, r: 'hat',     v: 100 },
    { b: 3,    r: 'perc',    v: 100 },
    { b: 3.5,  r: 'hat',     v: 100 },
    { b: 3.5,  r: 'perc',    v: 100 },
    { b: 4.5,  r: 'rim',     v: 100 },
    { b: 5,    r: 'kick',    v: 100 },
    { b: 5.5,  r: 'hat',     v: 100 },
    { b: 6,    r: 'snare',   v: 100 },
    { b: 6.5,  r: 'kick',    v: 100 },
    { b: 6.75, r: 'hat',     v: 100 },
    { b: 7.5,  r: 'hat',     v: 100 }
  ];
  var TOTAL_STEPS = LOOP_BEATS * 4;

  var ROLE_BY_LABEL = {
    kick: 'kick', '808': 'kick', sub: 'kick',
    snare: 'snare', clap: 'snare', snap: 'snare',
    hat: 'hat', closedhat: 'hat', hihat: 'hat',
    openhat: 'openhat', open: 'openhat',
    rim: 'rim', rimshot: 'rim',
    perc: 'perc', percussion: 'perc'
  };
  var ROLE_BY_INDEX = ['kick', 'snare', 'hat', 'openhat', 'rim', 'perc'];

  function roleFor(label, index) {
    var key = String(label || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return ROLE_BY_LABEL[key] || ROLE_BY_INDEX[index] || null;
  }

  function initPads(root) {
    if (root.dataset.mmPadsReady === 'true') return;
    root.dataset.mmPadsReady = 'true';

    var grid = root.querySelector('[data-mm-pads-grid]') || root;
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-mm-pad]'));
    if (!buttons.length) return;

    var transport = root.querySelector('[data-mm-transport]');
    var stepWrap = root.querySelector('[data-mm-steps]');
    var stepEls = [];

    /* ---------------------------------------------------------------------
       No Web Audio: keep the pads audible with plain media elements. The
       waveforms and the sequencer are silently skipped rather than shipping
       a broken-looking instrument.
       --------------------------------------------------------------------- */
    if (!AudioCtx) {
      if (transport) transport.hidden = true;
      buttons.forEach(function (btn) {
        var el = new Audio(btn.dataset.mmPad);
        el.preload = 'none';
        btn.addEventListener('pointerdown', function (e) {
          e.preventDefault();
          try { el.currentTime = 0; el.play(); } catch (err) {}
          btn.classList.add('is-hit');
          setTimeout(function () { btn.classList.remove('is-hit'); }, 140);
        });
      });
      return;
    }

    var ctx = new AudioCtx();
    var master = ctx.createGain();
    /* 0.8, not 1.0, and deliberately. Six pads can overlap — the loop stacks
       kick, hat and perc inside one 16th — and the perc pad carries a 1.3
       trim of its own, so unity here clips on the busy bars. The mute switch
       was why the pads were silent, not this number. */
    master.gain.value = 0.8;
    master.connect(ctx.destination);

    /* Colours come out of the stylesheet rather than being repeated here, so
       michelangelo-polish.css stays the single source of truth even for
       pixels drawn on a canvas. */
    function palette() {
      var cs = getComputedStyle(root);
      return {
        idle: (cs.getPropertyValue('--mm-ink-faint') || '').trim() || '#6C7080',
        live: (cs.getPropertyValue('--mm-accent') || '').trim() || '#2E5BFF'
      };
    }
    var COL = palette();

    var pads = buttons.map(function (btn, i) {
      var gain = parseFloat(btn.dataset.mmPadGain);
      return {
        btn: btn,
        canvas: btn.querySelector('[data-mm-wave]'),
        url: btn.dataset.mmPad,
        gain: isNaN(gain) ? 1 : gain,
        role: roleFor(btn.dataset.mmPadLabel, i),
        buffer: null,
        offset: 0,
        dur: 0.3,
        peaks: null,
        reveal: REDUCED ? 1 : -i * 0.16,
        progress: -1,
        active: false,
        startedAt: 0,
        litUntil: 0,
        queue: [],
        waiting: false,
        dpr: 1
      };
    });

    /* ---------------------------------------------------------------------
       Waveform
       --------------------------------------------------------------------- */

    /* Peak-per-column across both channels, then normalised per pad. The
       normalise is deliberate: it means a quiet rim still draws a readable
       shape instead of a flat line. The bars describe envelope, not level. */
    function peaksOf(buffer, n) {
      var a = buffer.getChannelData(0);
      var b = buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : null;
      var out = new Float32Array(n);
      var block = Math.max(1, Math.floor(a.length / n));
      var max = 0;
      for (var i = 0; i < n; i++) {
        var s = i * block;
        var e = Math.min(a.length, s + block);
        var p = 0;
        for (var j = s; j < e; j++) {
          var v = a[j] < 0 ? -a[j] : a[j];
          if (v > p) p = v;
          if (b) { var w = b[j] < 0 ? -b[j] : b[j]; if (w > p) p = w; }
        }
        out[i] = p;
        if (p > max) max = p;
      }
      if (max > 0) for (i = 0; i < n; i++) out[i] /= max;
      return out;
    }

    function sizeCanvas(p) {
      if (!p.canvas) return;
      var rect = p.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      p.dpr = dpr;
      p.canvas.width = Math.round(rect.width * dpr);
      p.canvas.height = Math.round(rect.height * dpr);
      if (p.buffer) {
        var cols = Math.max(10, Math.round(rect.width / 4));
        p.peaks = peaksOf(p.buffer, cols);
      }
      draw(p);
    }

    function draw(p) {
      if (!p.canvas || !p.canvas.getContext) return;
      var g = p.canvas.getContext('2d');
      var w = p.canvas.width;
      var h = p.canvas.height;
      g.clearRect(0, 0, w, h);
      if (!p.peaks) return;

      var n = p.peaks.length;
      var slot = w / n;
      var barW = Math.max(p.dpr, slot * 0.6);
      var mid = h / 2;
      var reveal = p.reveal < 0 ? 0 : p.reveal;

      for (var i = 0; i < n; i++) {
        var frac = i / n;
        if (frac > reveal) break;
        var bh = Math.max(p.dpr, p.peaks[i] * h * 0.88);
        /* No globalAlpha here. --mm-ink-faint already carries its own alpha
           (rgba(...,.55)), so applying a second one washed the idle bars out
           to roughly 28% and the waveform read as barely there. */
        g.fillStyle = p.progress >= 0 && frac <= p.progress ? COL.live : COL.idle;
        g.fillRect(i * slot, mid - bh / 2, barW, bh);
      }
    }

    /* ---------------------------------------------------------------------
       One shared frame loop. It only runs while something is actually
       moving, so an idle section costs nothing.
       --------------------------------------------------------------------- */
    var rafId = 0;

    function frame() {
      var busy = false;
      var now = ctx.currentTime;

      for (var i = 0; i < pads.length; i++) {
        var p = pads[i];
        var redraw = false;

        while (p.queue.length && p.queue[0] <= now) {
          p.startedAt = p.queue.shift();
          p.active = true;
          /* A 51ms closed hat would otherwise cut its own strike ring off
             after two frames, so the shortest sounds would flash least. Hold
             the lit state for a readable minimum instead of tying it to
             sample length. */
          p.litUntil = p.startedAt + Math.min(0.45, Math.max(0.14, p.dur));
          /* Toggling the class off, forcing layout, then on again is what
             restarts the CSS animation on a repeated strike. */
          p.btn.classList.remove('is-hit');
          void p.btn.offsetWidth;
          p.btn.classList.add('is-hit');
        }

        if (p.active) {
          p.progress = (now - p.startedAt) / p.dur;
          if (p.progress >= 1) {
            p.progress = -1;
            p.active = false;
          }
          redraw = true;
          busy = true;
        }

        if (p.litUntil) {
          if (now >= p.litUntil) {
            p.litUntil = 0;
            p.btn.classList.remove('is-hit');
            redraw = true;
          } else {
            busy = true;
          }
        }

        if (p.reveal < 1) {
          p.reveal += 0.05;
          if (p.reveal > 1) p.reveal = 1;
          redraw = true;
          busy = true;
        }

        if (redraw) draw(p);
      }

      /* The playhead is computed from elapsed time rather than advanced by a
         queue of events, so it sweeps every step evenly instead of only the
         ones that happen to carry a hit. */
      if (seq.running) {
        var elapsed = now - seq.origin;
        if (elapsed < 0) {
          if (seq.lastStep !== -1) { seq.lastStep = -1; setStep(-1); }
        } else {
          var st = Math.floor(elapsed / (secPerBeat() / 4)) % TOTAL_STEPS;
          if (st !== seq.lastStep) { seq.lastStep = st; setStep(st); }
        }
        busy = true;
      }

      rafId = busy ? requestAnimationFrame(frame) : 0;
    }

    function wake() { if (!rafId) rafId = requestAnimationFrame(frame); }

    /* ---------------------------------------------------------------------
       Loading
       --------------------------------------------------------------------- */
    var loadStarted = false;

    function load() {
      if (loadStarted) return;
      loadStarted = true;

      pads.forEach(function (p) {
        if (!p.url) return;
        fetch(p.url)
          .then(function (res) { return res.arrayBuffer(); })
          .then(function (raw) {
            return new Promise(function (resolve, reject) {
              // Callback form as well as the promise, because older Safari
              // only implements the callback signature.
              var maybe = ctx.decodeAudioData(raw, resolve, reject);
              if (maybe && maybe.then) maybe.then(resolve, reject);
            });
          })
          .then(function (buffer) {
            p.buffer = buffer;
            p.offset = firstTransient(buffer);
            p.dur = Math.max(0.08, buffer.duration - p.offset);
            sizeCanvas(p);
            if (p.waiting) { p.waiting = false; fire(p, 0); }
            wake();
          })
          .catch(function () {
            /* A sample that will not load leaves its pad silent but still
               visually responsive. Nothing else on the page is affected. */
          });
      });
    }

    /* MP3 decoding can leave a few ms of encoder padding at the head, and a
       source file may carry its own lead-in. Starting at the first sample
       that actually moves means the transient lands on the frame the pad is
       struck. Chrome already strips the padding; Safari has not always. */
    function firstTransient(buffer) {
      var d = buffer.getChannelData(0);
      var limit = Math.min(d.length, Math.floor(buffer.sampleRate * 0.25));
      for (var i = 0; i < limit; i++) {
        if (d[i] > 0.004 || d[i] < -0.004) {
          return Math.max(0, (i - 16) / buffer.sampleRate);
        }
      }
      return 0;
    }

    /* ---------------------------------------------------------------------
       Playback
       --------------------------------------------------------------------- */
    function fire(p, when, vel) {
      if (!p.buffer) { p.waiting = true; return; }
      var src = ctx.createBufferSource();
      src.buffer = p.buffer;
      var g = ctx.createGain();
      /* MIDI velocity rides on top of the pad's own level trim, so a ghost
         note in a future export gets quieter instead of being flattened. */
      g.gain.value = p.gain * (vel === undefined ? 1 : vel);
      src.connect(g);
      g.connect(master);
      var at = when || 0;
      src.start(at, p.offset);
      p.queue.push(at || ctx.currentTime);
      wake();
    }

    /* unlockOutput() BEFORE resume(), and both inside the gesture. The audio
       session type has to be set while the user activation is still live or
       WebKit ignores it and the mute switch keeps winning. */
    function hit(p) {
      load();
      unlockOutput();
      if (ctx.state === 'suspended') ctx.resume();
      fire(p, 0);
    }

    /* ---------------------------------------------------------------------
       Sequencer
       --------------------------------------------------------------------- */
    var seq = { running: false, origin: 0, base: 0, idx: 0, timer: 0, lastStep: -1 };
    var bpm = parseFloat(root.dataset.mmBpm) || 140;

    function secPerBeat() { return 60 / bpm; }

    var padByRole = {};
    pads.forEach(function (p) {
      if (p.role && !padByRole[p.role]) padByRole[p.role] = p;
    });

    /* The strip is built here rather than in Liquid so its length always
       matches the pattern. A hard-coded 16 would have been silently wrong the
       moment the loop turned out to be two bars. */
    function buildSteps() {
      if (!stepWrap) return;
      stepWrap.innerHTML = '';
      stepEls = [];
      for (var i = 0; i < TOTAL_STEPS; i++) {
        var li = document.createElement('li');
        var cls = 'mm-pads__step';
        if (i % 16 === 0) cls += ' mm-pads__step--bar';
        else if (i % 4 === 0) cls += ' mm-pads__step--beat';
        li.className = cls;
        stepWrap.appendChild(li);
        stepEls.push(li);
      }
    }

    function setStep(i) {
      for (var k = 0; k < stepEls.length; k++) {
        stepEls[k].classList.toggle('is-on', k === i);
      }
    }

    /* Walks the event list, wrapping to the next loop when it runs off the
       end, and queues everything starting inside the lookahead window. */
    function schedule() {
      if (!EVENTS.length) return;
      var horizon = ctx.currentTime + LOOKAHEAD;
      var spb = secPerBeat();
      var loopDur = LOOP_BEATS * spb;
      var guard = 0;
      while (guard++ < 512) {
        if (seq.idx >= EVENTS.length) {
          seq.idx = 0;
          seq.base += loopDur;
        }
        var ev = EVENTS[seq.idx];
        var when = seq.base + ev.b * spb;
        if (when >= horizon) break;
        var p = padByRole[ev.r];
        if (p) fire(p, when, ev.v / 100);
        seq.idx++;
      }
    }

    function startSeq() {
      load();
      unlockOutput();
      if (ctx.state === 'suspended') ctx.resume();
      seq.running = true;
      seq.origin = ctx.currentTime + 0.08;
      seq.base = seq.origin;
      seq.idx = 0;
      seq.lastStep = -1;
      seq.timer = setInterval(schedule, TICK_MS);
      schedule();
      root.classList.add('is-playing');
      if (transport) {
        transport.setAttribute('aria-pressed', 'true');
        transport.querySelector('[data-mm-transport-label]').textContent =
          transport.dataset.labelStop || 'Stop';
      }
      wake();
    }

    function stopSeq() {
      seq.running = false;
      clearInterval(seq.timer);
      seq.lastStep = -1;
      setStep(-1);
      root.classList.remove('is-playing');
      if (transport) {
        transport.setAttribute('aria-pressed', 'false');
        transport.querySelector('[data-mm-transport-label]').textContent =
          transport.dataset.labelPlay || 'Play the loop';
      }
    }

    if (transport) {
      transport.addEventListener('click', function () {
        if (seq.running) stopSeq(); else startSeq();
      });
    }

    /* ---------------------------------------------------------------------
       Input. pointerdown, not click: click fires on release and carries
       touch delay, and an instrument has to answer on the way down.
       --------------------------------------------------------------------- */
    grid.addEventListener('pointerdown', function (e) {
      var btn = e.target.closest('[data-mm-pad]');
      if (!btn) return;
      e.preventDefault();
      var p = pads[buttons.indexOf(btn)];
      if (p) hit(p);
    });

    grid.addEventListener('keydown', function (e) {
      if (e.key !== ' ' && e.key !== 'Enter') return;
      var btn = e.target.closest('[data-mm-pad]');
      if (!btn) return;
      e.preventDefault();
      var p = pads[buttons.indexOf(btn)];
      if (p) hit(p);
    });

    /* ---------------------------------------------------------------------
       Lifecycle
       --------------------------------------------------------------------- */
    buildSteps();

    var bpmEl = root.querySelector('[data-mm-bpmlabel]');
    if (bpmEl) bpmEl.textContent = bpm + ' BPM · ' + LOOP_BEATS / 4 + ' bars';

    pads.forEach(sizeCanvas);

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { pads.forEach(sizeCanvas); });
      ro.observe(grid);
    } else {
      window.addEventListener('resize', function () { pads.forEach(sizeCanvas); });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { load(); wake(); io.disconnect(); }
        });
      }, { rootMargin: '200px' });
      io.observe(root);
    } else {
      load();
    }

    /* Stop the loop if the visitor tabs away — a beat playing from a
       backgrounded tab is the kind of thing people close a site over. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && seq.running) stopSeq();
    });
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-mm-pads]').forEach(initPads);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
